/**
 * Numeric emulation helpers matching Flash Player's internal representations.
 *
 * TWIPS RULE (x/y quantization): Flash stores display-object translation as integer twips
 * (1/20 px). The AS3 setter converts with a C-style float->int cast of `value * 20`, i.e.
 * truncation toward zero (NaN -> 0, out-of-range saturates). This reproduces the well-known Flash
 * quirks: `mc.x += 0.04` never moves the clip (0.8 twip truncates to 0), `mc.x = 4.35` reads back
 * as 4.3 (4.35*20 = 86.999999... -> 86), and `mc.x = -0.07` reads back as -0.05. Reference:
 * Ruffle `Twips::from_pixels` (`(pixels * 20.0) as i32`) which is validated against Flash output.
 *
 * Matrix a/b/c/d are stored as float32 (Math.fround), alpha/color-transform multipliers as 8.8
 * fixed point (truncated), color offsets as int16, like Flash Player / Ruffle.
 */

export const TWIPS = 20;
const I32_MAX = 2147483647;
const I32_MIN = -2147483648;

/** Pixels -> twips with Flash truncation semantics. */
export function toTwips(px: number): number {
  const t = px * TWIPS;
  if (t !== t) return 0;
  if (t >= I32_MAX) return I32_MAX;
  if (t <= I32_MIN) return I32_MIN;
  return Math.trunc(t);
}

/** Twips -> pixels (lossless). */
export function fromTwips(tw: number): number {
  return tw / TWIPS;
}

/** ECMAScript ToInt32. */
export function toInt32(v: number): number {
  return v | 0;
}

/** ECMAScript ToUint32. */
export function toUint32(v: number): number {
  return v >>> 0;
}

/** IEEE round-half-to-even to int32; NaN/out-of-range -> i32::MIN (Ruffle `round_to_even`). */
export function roundToEven(n: number): number {
  if (!Number.isFinite(n) || n >= 2147483648 || n < -2147483648.5) return I32_MIN;
  const r = Math.round(n);
  if (Math.abs(n % 1) === 0.5) return 2 * Math.round(n / 2);
  return r;
}

/** f32 round-ties-even to i32 used by matrix point transforms (NaN/Inf -> 0, overflow -> MIN). */
export function f32RoundToI32(f: number): number {
  if (!Number.isFinite(f)) return 0;
  if (f >= 2147483648) return I32_MIN;
  if (Math.abs(f % 1) === 0.5) return 2 * Math.round(f / 2);
  return Math.round(f);
}

/** Number -> 8.8 fixed point (i16, truncating, saturating). */
export function toFixed8(v: number): number {
  const t = v * 256;
  if (t !== t) return 0;
  if (t >= 32767) return 32767;
  if (t <= -32768) return -32768;
  return Math.trunc(t);
}

/** Number -> i16 (truncating, saturating like Rust `as i16`). */
export function toI16(v: number): number {
  if (v !== v) return 0;
  if (v >= 32767) return 32767;
  if (v <= -32768) return -32768;
  return Math.trunc(v);
}

export const fround = Math.fround;

/** Internal display matrix: float32 a..d, integer twips tx/ty. */
export interface TMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
}

export const IDENTITY: Readonly<TMatrix> = Object.freeze({ a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 });

export function tmat(a = 1, b = 0, c = 0, d = 1, tx = 0, ty = 0): TMatrix {
  return { a, b, c, d, tx, ty };
}

/** m1 * m2 (apply m2 first, then m1), float32 arithmetic like Ruffle's Matrix::mul. */
export function tmatMul(m1: TMatrix, m2: TMatrix): TMatrix {
  return {
    a: fround(fround(m1.a * m2.a) + fround(m1.c * m2.b)),
    b: fround(fround(m1.b * m2.a) + fround(m1.d * m2.b)),
    c: fround(fround(m1.a * m2.c) + fround(m1.c * m2.d)),
    d: fround(fround(m1.b * m2.c) + fround(m1.d * m2.d)),
    tx: (f32RoundToI32(fround(fround(m1.a * m2.tx) + fround(m1.c * m2.ty))) + m1.tx) | 0,
    ty: (f32RoundToI32(fround(fround(m1.b * m2.tx) + fround(m1.d * m2.ty))) + m1.ty) | 0,
  };
}

export function tmatInverse(m: TMatrix): TMatrix | null {
  const tx = m.tx;
  const ty = m.ty;
  const det = fround(fround(m.a * m.d) - fround(m.b * m.c));
  if (Math.abs(det) <= 1.1920929e-7) return null;
  return {
    a: fround(m.d / det),
    b: fround(m.b / -det),
    c: fround(m.c / -det),
    d: fround(m.a / det),
    tx: f32RoundToI32(fround(fround(fround(m.d * tx) - fround(m.c * ty)) / -det)),
    ty: f32RoundToI32(fround(fround(fround(m.b * tx) - fround(m.a * ty)) / det)),
  };
}

/** Transform a twips point. */
export function tmatPoint(m: TMatrix, x: number, y: number): [number, number] {
  return [
    (f32RoundToI32(fround(fround(m.a * x) + fround(m.c * y))) + m.tx) | 0,
    (f32RoundToI32(fround(fround(m.b * x) + fround(m.d * y))) + m.ty) | 0,
  ];
}

/** Twips rectangle; `xMin > xMax` means invalid/empty (Ruffle Rectangle::INVALID). */
export interface TRect {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
}

export function invalidRect(): TRect {
  return { xMin: I32_MAX, yMin: I32_MAX, xMax: I32_MIN, yMax: I32_MIN };
}

export function rectValid(r: TRect): boolean {
  return r.xMin <= r.xMax && r.yMin <= r.yMax;
}

export function rectUnion(a: TRect, b: TRect): TRect {
  if (!rectValid(a)) return rectValid(b) ? { ...b } : a;
  if (!rectValid(b)) return a;
  return {
    xMin: Math.min(a.xMin, b.xMin),
    yMin: Math.min(a.yMin, b.yMin),
    xMax: Math.max(a.xMax, b.xMax),
    yMax: Math.max(a.yMax, b.yMax),
  };
}

export function tmatRect(m: TMatrix, r: TRect): TRect {
  if (!rectValid(r)) return invalidRect();
  const [x0, y0] = tmatPoint(m, r.xMin, r.yMin);
  const [x1, y1] = tmatPoint(m, r.xMin, r.yMax);
  const [x2, y2] = tmatPoint(m, r.xMax, r.yMin);
  const [x3, y3] = tmatPoint(m, r.xMax, r.yMax);
  return {
    xMin: Math.min(x0, x1, x2, x3),
    xMax: Math.max(x0, x1, x2, x3),
    yMin: Math.min(y0, y1, y2, y3),
    yMax: Math.max(y0, y1, y2, y3),
  };
}

export function rectContains(r: TRect, x: number, y: number): boolean {
  return x >= r.xMin && x <= r.xMax && y >= r.yMin && y <= r.yMax;
}

/** Pixel bounds (manifest) -> twips rect (rounded to the nearest twip). */
export function boundsToTwips(b: { xMin: number; yMin: number; xMax: number; yMax: number }): TRect {
  return {
    xMin: Math.round(b.xMin * TWIPS),
    yMin: Math.round(b.yMin * TWIPS),
    xMax: Math.round(b.xMax * TWIPS),
    yMax: Math.round(b.yMax * TWIPS),
  };
}

export function rectWidthPx(r: TRect): number {
  return rectValid(r) ? (r.xMax - r.xMin) / TWIPS : 0;
}

export function rectHeightPx(r: TRect): number {
  return rectValid(r) ? (r.yMax - r.yMin) / TWIPS : 0;
}

/** Double-precision matrix transform (used for rendering / point hit tests in pixels). */
export function applyMatrixPx(m: TMatrix, x: number, y: number): [number, number] {
  return [m.a * x + m.c * y + m.tx / TWIPS, m.b * x + m.d * y + m.ty / TWIPS];
}
